import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight, FaCalendarAlt, FaUser, FaArrowLeft } from 'react-icons/fa';
import Header from './Header';
import api from './services/apiService';
import './scss/matchReports.scss';
import './scss/Dashboard.scss';
import './scss/matchReportsPage.scss';

const MatchReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMatchReports = async () => {
      try {
        setLoading(true);
        const teamId = sessionStorage.getItem('selected_team_id');
        
        if (!teamId) {
          setError('No team selected');
          setLoading(false);
          return;
        }

        // Build the API URL - simplified to show all team match reports
        const apiUrl = `${process.env.REACT_APP_API_URL}/get_team_match_reports.php?team_id=${teamId}`;
        
        const response = await api.get(apiUrl);
        
        if (response.data.success) {
          setReports(response.data.reports || []);
        } else {
          setError(response.data.message || 'Failed to fetch match reports');
        }
      } catch (err) {
        console.error('Error fetching match reports:', err);
        setError('Failed to load match reports');
      } finally {
        setLoading(false);
      }
    };

    fetchMatchReports();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleReportClick = (matchId) => {
    navigate(`/match/${matchId}/report`);
  };

  const handleBackClick = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="match-reports-page container">
        <Header />
        <button className="back-button" onClick={() => window.history.back()}>← Back</button>
        <h1>Match Reports</h1>
        <p style={{color: '#585858', marginTop: '0'}}>Loading match reports...</p>
      </div>
    );
  }

  if (error) {
    return (
        <div className="match-reports-page container">
            <Header />
            <button className="back-button" onClick={() => window.history.back()}>← Back</button>
            <h1>Match Reports</h1>
            <p style={{color: '#585858', marginTop: '0'}}>{error}</p>
        </div>
    );
  }

  if (reports.length === 0) {
    // Add dummy match report data for display purposes
    const dummyReports = [
      {
        report_id: 'dummy-1',
        match_id: '1',
        title: 'Thrilling 3-2 Victory Against FC United',
        excerpt: 'An exciting match that showcased excellent teamwork and determination. The team showed great character to come back from behind twice and secure a well-deserved victory.',
        match_date: '2025-07-30',
        created_by: 'Coach',
        opposition: 'FC United',
        image_url: 'https://media.istockphoto.com/id/1620960870/photo/happy-teenage-female-soccer-team-goal-celebration-run-and-congratulations-cheer.jpg?s=612x612&w=0&k=20&c=rPZ-6uJzgoxaiFTFloRoyUGoOP31On-i98jXdARwahk='
      }
    ];

    return (
      <div className="match-reports-page container">
        <Header />
        <button className="back-button" onClick={() => window.history.back()}>← Back</button>
        <h1>Match Reports <span className="example-label">Example</span></h1>
        <div className="matches-container dashboard">
          <div className="matches-scroll-container">
            <div className="matches-scroll-wrapper">
              {dummyReports.map((report, index) => (
                <div className="report-card-wrapper" key={report.report_id}>
                  <div 
                    className="report-card"
                    onClick={() => navigate(`/match/${report.match_id}/report`)}
                  >
                    <div className="report-image-container">
                      <img 
                        src={report.image_url} 
                        alt={report.title} 
                        className="report-image"
                      />
                      {report.opposition && (
                        <div className="report-opposition">
                          vs {report.opposition}
                        </div>
                      )}
                    </div>
                    <div className="report-content">
                      <h3 className="report-title">{report.title}</h3>
                      <p className="report-excerpt">{report.excerpt}</p>
                      <div className="report-meta">
                        <div className="report-date">
                          <FaCalendarAlt />
                          <span>{formatDate(report.match_date)}</span>
                        </div>
                        <div className="report-author">
                          <FaUser />
                          <span>{report.created_by}</span>
                        </div>
                      </div>
                      <button className="read-more-button">
                        Read match report <FaArrowRight />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="match-reports-page container">
        <Header />
        <button className="back-button" onClick={() => window.history.back()}>← Back</button>
        <h1>Match Reports</h1>
        
        <div className="matches-container dashboard">
          <div className="matches-scroll-container">
            <div className="matches-scroll-wrapper">
              {reports.map(report => (
                <div className="report-card-wrapper" key={report.report_id}>
                  <div 
                    className="report-card"
                    onClick={() => handleReportClick(report.match_id)}
                  >
                    <div className="report-image-container">
                      {report.image_url ? (
                        <img 
                          src={report.image_url} 
                          alt={report.title} 
                          className="report-image"
                        />
                      ) : (
                        <div className="report-image-placeholder">
                          <FaCalendarAlt />
                        </div>
                      )}
                      {report.opposition && (
                        <div className="report-opposition">
                          vs {report.opposition}
                        </div>
                      )}
                    </div>
                    <div className="report-content">
                      <h3 className="report-title">{report.title}</h3>
                      <p className="report-excerpt">{report.excerpt}</p>
                      <div className="report-meta">
                        <div className="report-date">
                          <FaCalendarAlt />
                          <span>{formatDate(report.match_date)}</span>
                        </div>
                        <div className="report-author">
                          <FaUser />
                          <span>{report.created_by}</span>
                        </div>
                      </div>
                      <button className="read-more-button">
                        Read match report <FaArrowRight />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
  );
};

export default MatchReportsPage; 